import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import api from '../utils/api';
import { toast } from 'sonner';
import CosmicBackground from '../components/CosmicBackground';
import Logo from '../components/Logo';
import { LogOut, Users, MessageSquare, Crown, Shield, ArrowLeft } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useTranslation } from '../utils/translations';

const Admin = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { t } = useTranslation(language);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    
    if (!token || !userData) {
      navigate('/login');
      return;
    }

    const user = JSON.parse(userData);
    if (!user.is_admin) {
      navigate('/dashboard');
      return;
    }

    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    try {
      const [statsRes, usersRes] = await Promise.all([
        api.get('/admin/stats'),
        api.get('/admin/users')
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      toast.error('Failed to fetch admin data');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateUser = async (userId, updates) => {
    try {
      await api.patch(`/admin/users/${userId}`, updates);
      toast.success('User updated successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to update user');
    }
  };

  const handleLogout = async () => {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      console.error('Logout API call failed:', error);
    }
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen relative">
      <CosmicBackground />
      <div className="fixed inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-violet-900/20 via-slate-900 to-slate-950 pointer-events-none" style={{ zIndex: 1 }} />
      
      <div className="relative z-10">
        {/* Header */}
        <header className="py-6 px-6 sm:px-12 border-b border-white/5">
          <div className="max-w-7xl mx-auto flex justify-between items-center">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="text-slate-400 hover:text-white transition-colors"
                data-testid="back-to-dashboard"
                title={t('backToDashboard')}
              >
                <ArrowLeft size={24} />
              </button>
              <Logo size="md" />
            </div>
            <button
              onClick={handleLogout}
              className="text-slate-400 hover:text-white transition-colors"
              data-testid="logout-button"
            >
              <LogOut size={20} />
            </button>
          </div>
        </header>

        <div className="max-w-7xl mx-auto px-6 py-12 space-y-8">
          {/* Stats */}
          {stats && (
            <div className="grid md:grid-cols-4 gap-6">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="glass-card p-6"
                data-testid="stat-total-users"
              >
                <Users className="w-8 h-8 text-violet-500 mb-3" />
                <div className="text-3xl font-bold text-white mb-1">{stats.total_users}</div>
                <div className="text-sm text-slate-400">{t('totalUsers')}</div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="glass-card p-6"
                data-testid="stat-free-users"
              >
                <Users className="w-8 h-8 text-slate-500 mb-3" />
                <div className="text-3xl font-bold text-white mb-1">{stats.free_users}</div>
                <div className="text-sm text-slate-400">{t('freeUsers')}</div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="glass-card p-6"
                data-testid="stat-premium-users"
              >
                <Crown className="w-8 h-8 text-amber-500 mb-3" />
                <div className="text-3xl font-bold text-white mb-1">{stats.premium_users}</div>
                <div className="text-sm text-slate-400">{t('premiumUsers')}</div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="glass-card p-6"
                data-testid="stat-total-questions"
              >
                <MessageSquare className="w-8 h-8 text-cyan-500 mb-3" />
                <div className="text-3xl font-bold text-white mb-1">{stats.total_questions}</div>
                <div className="text-sm text-slate-400">{t('totalQuestions')}</div>
              </motion.div>
            </div>
          )}

          {/* Users Table */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="glass-card p-6 overflow-x-auto"
            data-testid="users-table"
          >
            <h2 className="font-heading text-2xl font-bold text-white mb-6">{t('users')}</h2>
            <table className="w-full">
              <thead>
                <tr className="text-left border-b border-white/10">
                  <th className="pb-3 text-slate-400 font-medium">{t('name')}</th>
                  <th className="pb-3 text-slate-400 font-medium">{t('email')}</th>
                  <th className="pb-3 text-slate-400 font-medium">{t('tier')}</th>
                  <th className="pb-3 text-slate-400 font-medium">{t('admin')}</th>
                  <th className="pb-3 text-slate-400 font-medium">{t('actions')}</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user, idx) => (
                  <tr key={user.id} className="border-b border-white/5" data-testid={`user-row-${idx}`}>
                    <td className="py-4 text-white">{user.full_name}</td>
                    <td className="py-4 text-slate-400">{user.email}</td>
                    <td className="py-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        user.subscription_tier === 'premium' 
                          ? 'bg-amber-500/20 text-amber-400' 
                          : 'bg-slate-500/20 text-slate-400'
                      }`}>
                        {user.subscription_tier}
                      </span>
                    </td>
                    <td className="py-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        user.is_admin 
                          ? 'bg-violet-500/20 text-violet-400' 
                          : 'bg-slate-500/20 text-slate-400'
                      }`}>
                        {user.is_admin ? t('yes') : t('no')}
                      </span>
                    </td>
                    <td className="py-4">
                      <div className="flex gap-2">
                        {user.subscription_tier === 'free' && (
                          <button
                            onClick={() => handleUpdateUser(user.id, { subscription_tier: 'premium' })}
                            className="text-xs px-3 py-1 bg-amber-500/20 text-amber-400 rounded hover:bg-amber-500/30 transition-colors"
                            data-testid={`upgrade-user-${idx}`}
                          >
                            {t('makePremium')}
                          </button>
                        )}
                        {!user.is_admin && (
                          <button
                            onClick={() => handleUpdateUser(user.id, { is_admin: true })}
                            className="text-xs px-3 py-1 bg-violet-500/20 text-violet-400 rounded hover:bg-violet-500/30 transition-colors"
                            data-testid={`make-admin-${idx}`}
                          >
                            {t('makeAdmin')}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Admin;